import { useState } from 'react';
import { Box, Button, Drawer, FormControlLabel, Stack, Switch, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import ErrorOutlineOutlined from '@mui/icons-material/ErrorOutlineOutlined';
import FactCheckOutlined from '@mui/icons-material/FactCheckOutlined';
import FolderOpenOutlined from '@mui/icons-material/FolderOpenOutlined';
import InsertDriveFileOutlined from '@mui/icons-material/InsertDriveFileOutlined';
import { FilterBar, FilterSelect, PagedFooter, PageHeader } from '../components/ListControls';
import { Panel } from '../components/Panel';
import { useNotice } from '../components/NoticeProvider';
import { StatTile } from '../components/StatTile';
import { TabStrip } from '../components/TabStrip';
import { AddDocumentDialog } from '../features/documents/AddDocumentDialog';
import { DocumentDetail } from '../features/documents/DocumentDetail';
import { DocumentsTable } from '../features/documents/DocumentsTable';
import { DOC_SOURCES, DOC_STATUSES, type DocStatus } from '../features/documents/sample';
import { useDocuments } from '../features/documents/useDocuments';
import { isWorkingFile } from '../features/documents/workingFiles';
import { SHIPMENTS } from '../features/shipments/sample';
import { FINDER_NOTICE, useCanOpenFinder } from '../lib/finder';

const SIZE = 8;
const TABS = ['All Documents', 'By Shipment', 'Unassigned'] as const;
type Tab = (typeof TABS)[number];

/** The document register: a record of every document. The files themselves stay in Finder on your Mac. */
export function DocumentsPage() {
  const docs = useDocuments();
  const notice = useNotice();
  const canFinder = useCanOpenFinder();
  const { id } = useParams();
  const [tab, setTab] = useState<Tab>('All Documents');
  const [openId, setOpenId] = useState<string>();
  const [adding, setAdding] = useState(false);
  const [search, setSearch] = useState('');
  const [shipment, setShipment] = useState('all');
  const [type, setType] = useState('all');
  const [source, setSource] = useState('all');
  const [status, setStatus] = useState('all');
  const [showWorking, setShowWorking] = useState(false);
  const [page, setPage] = useState(1);
  const filter =
    <T,>(set: (v: T) => void) =>
    (v: T) => {
      set(v);
      setPage(1);
    };

  const working = docs.items.filter(isWorkingFile).length;
  const items = showWorking ? docs.items : docs.items.filter((r) => !isWorkingFile(r));
  const types = [...new Set(items.map((r) => r.type))].sort();
  const unassigned = items.filter((r) => !r.srb);
  const w = search.trim().toLowerCase();
  const shown = (tab === 'Unassigned' ? unassigned : items)
    .filter((r) => shipment === 'all' || r.srb === shipment)
    .filter((r) => type === 'all' || r.type === type)
    .filter((r) => source === 'all' || r.source === source)
    .filter((r) => status === 'all' || r.status === status)
    .filter((r) => !w || [r.name, r.srb, r.type, r.source, r.notes].join(' ').toLowerCase().includes(w));
  const first = (page - 1) * SIZE;
  const rows = shown.slice(first, first + SIZE);
  const open = docs.items.find((r) => r.id === (openId ?? id));
  const count = (s: DocStatus) => items.filter((r) => r.status === s).length;
  const only = (s: DocStatus) => () => {
    setTab('All Documents');
    filter(setStatus)(s);
  };
  const shipmentsWithDocs = [...new Set(items.map((r) => r.srb).filter(Boolean))];

  return (
    <Stack spacing={2}>
      <PageHeader
        title="Documents"
        subtitle="Track and manage shipment documents."
        newLabel={docs.editable ? 'Add Document Record' : undefined}
        noExport
        onNew={() => setAdding(true)}
      >
        {canFinder && (
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<FolderOpenOutlined />}
            onClick={() => notice(FINDER_NOTICE, 'info')}
          >
            Open Shipments Folder
          </Button>
        )}
      </PageHeader>

      <Box
        sx={{
          display: 'grid',
          gap: { xs: 1.25, md: 2 },
          gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' },
        }}
      >
        <StatTile
          count={items.length}
          title="All Documents"
          note={`Across ${shipmentsWithDocs.length} shipments`}
          icon={<InsertDriveFileOutlined />}
          tone="info"
          onClick={() => {
            setTab('All Documents');
            filter(setStatus)('all');
          }}
        />
        <StatTile
          count={count('Missing')}
          title="Missing Documents"
          note="Required for active shipments"
          icon={<ErrorOutlineOutlined />}
          tone="error"
          onClick={only('Missing')}
        />
        <StatTile
          count={count('Needs Review')}
          title="Needs Review"
          note="Awaiting your verification"
          icon={<FactCheckOutlined />}
          tone="warning"
          onClick={only('Needs Review')}
        />
      </Box>

      <TabStrip
        tabs={TABS.map((t) => ({ value: t, label: t === 'Unassigned' ? `Unassigned (${unassigned.length})` : t }))}
        value={tab}
        onChange={filter(setTab)}
      />

      <FilterBar placeholder="Search documents…" search={search} onSearch={filter(setSearch)}>
        <FilterSelect
          label="Shipment"
          value={shipment}
          onChange={filter(setShipment)}
          options={shipmentsWithDocs.sort()}
        />
        <FilterSelect label="Type" value={type} onChange={filter(setType)} options={types} />
        <FilterSelect label="Source" value={source} onChange={filter(setSource)} options={DOC_SOURCES} />
        <FilterSelect label="Status" value={status} onChange={filter(setStatus)} options={DOC_STATUSES} />
        {working > 0 && (
          <FormControlLabel
            sx={{ m: 0, whiteSpace: 'nowrap' }}
            control={<Switch size="small" checked={showWorking} onChange={(_, v) => filter(setShowWorking)(v)} />}
            label={`Show ${working} working files`}
          />
        )}
      </FilterBar>

      {tab === 'By Shipment' ? (
        <Stack spacing={2}>
          {[...new Set(shown.filter((r) => r.srb).map((r) => r.srb))].sort().map((srb) => {
            const s = SHIPMENTS.find((x) => x.srb === srb);
            return (
              <Panel key={srb} title={srb} subtitle={s ? `${s.customer} · ${s.from.city} → ${s.to.city}` : undefined}>
                <DocumentsTable rows={shown.filter((r) => r.srb === srb)} onOpen={(r) => setOpenId(r.id)} />
              </Panel>
            );
          })}
          {!shown.some((r) => r.srb) && <Typography color="text.secondary">No documents match.</Typography>}
        </Stack>
      ) : (
        <>
          <DocumentsTable
            rows={rows}
            onOpen={(r) => setOpenId(r.id)}
            empty={items.length ? 'No documents match.' : 'No documents yet.'}
          />
          <PagedFooter noun="documents" total={shown.length} page={page} size={SIZE} onPage={setPage} />
        </>
      )}

      <Drawer
        anchor="right"
        open={Boolean(open)}
        onClose={() => setOpenId(undefined)}
        slotProps={{ paper: { sx: { width: { xs: '100%', sm: 440 } } } }}
      >
        {open && (
          <DocumentDetail
            r={open}
            onClose={() => setOpenId(undefined)}
            onDelete={docs.editable ? docs.remove : undefined}
          />
        )}
      </Drawer>
      {docs.editable && <AddDocumentDialog open={adding} onClose={() => setAdding(false)} onAdd={docs.add} />}
    </Stack>
  );
}
